/**
 * InventoryPanel — Cube Sheet + Room-by-Room inventory (Phase 8e).
 *
 * Opened as a full-screen overlay from the lead sticky header. Inventory
 * converts a list of items into total CuFT / weight that inform the broker; it
 * NEVER renders on a customer-facing document. The only path that writes the
 * lead's cubic_feet/weight_lbs is the "Apply totals to lead" button.
 *
 * The catalog (~170 items, 11 rooms) is read from the DB (seeded via migration
 * 005) — never hardcoded here. Cube Sheet and Room-by-Room share the same
 * underlying lead_inventory_items, so a qty change in one view reflects in the
 * other.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CRM, tintOf } from '../lib/theme';
import { numberInput } from '../lib/numberInput';
import { useApi, useApiClient } from '../hooks/useApi';
import { useRowSync, RowStatus } from '../hooks/useRowSync';
import { prepare, search as searchCatalog, normalize } from '../lib/inventorySearch';
import { computeInventoryTotals, CatalogItem } from '../lib/inventory';
import { Icons, CatalogIcon } from '../lib/icons';
import { formatJobNo } from '../lib/jobNumber';
import { formatDateTime } from '../lib/utils';

const C = { ...CRM, mono: "'DM Mono', monospace" };

interface InventoryPanelProps {
  leadId: string;
  lead: any;
  onClose: () => void;
  onApplied: () => void; // refetch the lead after Apply
}

type View = 'cube' | 'room';

/**
 * One line in the Room-by-Room view. `cat` is the catalog row it came from and
 * `item` the lead's row, and EITHER may be null: a catalog entry with no item is
 * an offer at quantity zero (shown while searching or showing all), and an item
 * with no catalog entry is a custom one. Both are lines a rep must be able to see
 * and set a quantity on.
 */
type RoomEntry = { key: string; cat: any | null; item: ItemRow | null };

// A merged line item: the lead_inventory_items row (if any) for a catalog id.
interface ItemRow {
  id: string;
  catalog_item_id?: string | null;
  custom_name?: string | null;
  custom_cuft?: number | null;
  custom_density?: number | null;
  quantity?: number | null;
  room_key?: string | null;
  notes?: string | null;
  /**
   * WHO PUT THE ROW HERE (migration 036). Defaults to 'rep' in the database, so
   * every row that predates P5a reads as ours — which is true.
   *
   * It matters for review, not for math: a rep corrects their own entry without
   * a thought and should think twice before overwriting what the client said
   * they own. Absent on a pre-036 payload, hence optional.
   */
  source?: 'rep' | 'client' | null;
  /** From useRowSync: the row's stable key and where its save has got to. */
  _key?: string;
  _status?: RowStatus;
  _error?: string | null;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt1 = (n: number) => (Math.round(n * 10) / 10).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmt0 = (n: number) => Math.round(n).toLocaleString();

export function InventoryPanel({ leadId, lead, onClose, onApplied }: InventoryPanelProps) {
  const api = useApiClient();
  const [view, setView] = useState<View>('cube');
  const [search, setSearch] = useState('');
  // Items only, until someone asks for the catalogue. See filteredCatalog.
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openRooms, setOpenRooms] = useState<Record<string, boolean>>({});

  const { data: catalogData } = useApi<{ catalog: any[]; rooms: any[] }>(
    async () => (await api.getInventoryCatalog()).data!,
    { dependencies: [leadId] }
  );
  const { data: inv, refresh: refreshInv } = useApi<{ header: any; items: ItemRow[]; media?: RepMedia[]; mediaTotals?: { count: number; bytes: number; label: string } }>(
    async () => (await api.getLeadInventory(leadId)).data!,
    { dependencies: [leadId] }
  );

  const catalog = catalogData?.catalog ?? [];
  const rooms = catalogData?.rooms ?? [];
  /**
   * EDITS ARE ON SCREEN AT ONCE AND SAVED BEHIND (useRowSync). Every quantity
   * change used to wait for its write and a refetch of the whole inventory, with
   * the steppers disabled in between — twenty quick changes took twenty round
   * trips each, and clicks made while disabled were lost. Now each row saves
   * ~600ms after its last change, one write per row in flight, and the panel
   * refreshes once when the batch has landed.
   *
   * The CuFT / weight footer is computed here from the rows on screen by the
   * same engine the server uses (lib/inventory), so it follows each change
   * immediately; it is marked "saving" until the batch is confirmed.
   */
  const sync = useRowSync<ItemRow>({
    create: async (draft) => (await api.addInventoryItem(leadId, draft)).data!.item.id,
    update: (id, patch) => api.updateInventoryItem(id, patch),
    remove: (id) => api.deleteInventoryItem(id),
    onSettled: refreshInv,
  });
  const items: ItemRow[] = sync.view(inv?.items ?? []);
  const keyOf = (it: ItemRow) => it._key ?? it.id;

  // EVERY catalogue row, hidden ones included: the CuFT / weight footer and
  // every line's name come from here, and a row already on this lead must keep
  // pricing and reading the same after its item is hidden (047).
  const catalogById = useMemo(() => {
    const m: Record<string, CatalogItem> = {};
    for (const c of catalog) m[c.id] = c;
    return m;
  }, [catalog]);

  /**
   * THE PICK-LIST: active items only (inventory_catalog.is_active, 047). Search,
   * Boxes / Misc and the room grids offer nothing from outside it. A hidden item
   * that is already on the lead still shows — as its own line in the cube sheet
   * and as a row in its room — because those are drawn from the lead's rows.
   * Before 047, is_active is absent and every item is active.
   */
  const activeCatalog = useMemo(() => catalog.filter((c: any) => c.is_active !== false), [catalog]);

  // Map catalog_item_id -> the single lead item row (Cube Sheet is one row per
  // catalog item). Custom items are kept separately.
  const itemByCatalogId = useMemo(() => {
    const m: Record<string, ItemRow> = {};
    for (const it of items) if (it.catalog_item_id) m[it.catalog_item_id] = it;
    return m;
  }, [items]);

  const customItems = useMemo(() => items.filter((it) => !it.catalog_item_id), [items]);

  // ---- Search (045): the same matcher the client's pages use ---------------
  // Exact name, then synonym (search_terms), then typo matches, ranked. A rep
  // looking for "couch" finds the sofa the client added as "couch".
  const preparedCatalog = useMemo(
    () => prepare(activeCatalog.map((c: any) => ({ ...c, searchTerms: c.search_terms ?? [] }))),
    [activeCatalog],
  );
  const q = search.trim();
  const hitRank = useMemo(
    () => (q ? new Map(searchCatalog(preparedCatalog, q).map((h, i) => [h.item.id, i])) : null),
    [q, preparedCatalog],
  );
  const matchesCat = (c: any) => !hitRank || hitRank.has(c.id);
  const byRank = (a: any, b: any) => (hitRank ? hitRank.get(a.id)! - hitRank.get(b.id)! : 0);
  const matchesName = (name: string) => !q || normalize(name).includes(normalize(q));

  // ---- Boxes / Misc (045/046): one section for the whole move --------------
  // Shown first, counted as the total across every row for the item whatever
  // room it was filed under, and never repeated inside a room.
  const sectionRoom = useMemo(() => rooms.find((r: any) => r.whole_move) ?? null, [rooms]);
  const sectionIds = useMemo(
    () => new Set<string>(sectionRoom ? activeCatalog.filter((c: any) => c.category === sectionRoom.room_key).map((c: any) => c.id) : []),
    [activeCatalog, sectionRoom],
  );
  const totalByCat: Record<string, number> = {};
  for (const it of items) if (it.catalog_item_id) totalByCat[it.catalog_item_id] = (totalByCat[it.catalog_item_id] ?? 0) + num(it.quantity);
  const sectionRows = useMemo(() => {
    if (!sectionRoom) return [];
    return activeCatalog
      .filter((c: any) => sectionIds.has(c.id) && matchesCat(c) && (q || showAllCatalog || (totalByCat[c.id] ?? 0) > 0))
      .sort(byRank)
      .map((c: any) => ({
        cat: c,
        qty: totalByCat[c.id] ?? 0,
        failed: items.find((it) => it.catalog_item_id === c.id && it._status === 'error') ?? null,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionRoom, activeCatalog, sectionIds, hitRank, showAllCatalog, items]);

  // Custom rows follow the search box like catalog rows do; they are never hidden
  // by the items-only default, because a custom row only exists if it is on the job.
  const filteredCustomItems = useMemo(
    () => (q ? customItems.filter((it) => matchesName(it.custom_name || '')) : customItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customItems, q],
  );

  /** Nothing in the catalogue and no custom row: offer to add exactly what was typed. */
  const noMatch = !!q && !!hitRank && hitRank.size === 0 && filteredCustomItems.length === 0;

  // Log it once the query has sat for 1.5s — the same signal the client pages
  // send, so Settings sees what reps look for too.
  const loggedMisses = useRef<Set<string>>(new Set());
  const logMiss = (term: string, kind: 'search' | 'add_own' = 'search') => {
    const key = `${kind}:${normalize(term)}`;
    if (normalize(term).length < 2 || loggedMisses.current.has(key)) return;
    loggedMisses.current.add(key);
    api.logInventorySearchMiss({ term, path: view === 'cube' ? 'rep_cube' : 'rep_room', kind, lead_id: leadId }).catch(() => {});
  };
  useEffect(() => {
    if (!noMatch) return;
    const t = window.setTimeout(() => logMiss(q), 1500);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noMatch, q]);

  const totals = useMemo(() => computeInventoryTotals(items, catalogById), [items, catalogById]);
  const itemCount = items.length;
  const pieceCount = items.reduce((s, it) => s + num(it.quantity), 0);

  // ---- Mutations ----------------------------------------------------------

  // Set the qty for a catalog item: create the row, patch it, or delete when 0.
  const setCatalogQty = (catId: string, qty: number, roomKey?: string | null) => {
    const existing = itemByCatalogId[catId];
    if (existing) {
      if (qty <= 0) sync.remove(keyOf(existing));
      else sync.edit(keyOf(existing), { quantity: qty });
    } else if (qty > 0) {
      sync.add({ catalog_item_id: catId, quantity: qty, room_key: roomKey ?? null });
    }
  };

  // Components hand back a row's id; useRowSync knows rows by their stable key.
  const keyForId = (id: string) => { const it = items.find((i) => i.id === id); return it ? keyOf(it) : id; };
  const patchItem = (id: string, patch: Record<string, any>) => sync.edit(keyForId(id), patch);
  const removeItem = (id: string) => sync.remove(keyForId(id));
  const retryItem = (id: string) => sync.retry(keyForId(id));

  const addCustom = (name = 'Custom item') =>
    sync.add({ custom_name: name, custom_cuft: 10, custom_density: 7, quantity: 1 });

  /**
   * A whole-move item's TOTAL, spread over its rows: up goes onto a row with no
   * room (made if need be), down comes off the no-room rows first and then the
   * room rows. The rep may take a count to zero — client rows included; the
   * rep is the one pricing it.
   */
  const setSectionTotal = (catId: string, n: number) => {
    const rows = items.filter((it) => it.catalog_item_id === catId);
    const current = rows.reduce((t, r) => t + num(r.quantity), 0);
    let delta = Math.max(0, Math.trunc(n)) - current;
    if (delta === 0) return;
    const loose = rows.filter((r) => !r.room_key);
    if (delta > 0) {
      if (loose[0]) sync.edit(keyOf(loose[0]), { quantity: num(loose[0].quantity) + delta });
      else sync.add({ catalog_item_id: catId, quantity: delta, room_key: null });
      return;
    }
    for (const r of [...loose, ...rows.filter((x) => x.room_key)]) {
      if (delta >= 0) break;
      const take = Math.min(num(r.quantity), -delta);
      const left = num(r.quantity) - take;
      if (left <= 0) sync.remove(keyOf(r)); else sync.edit(keyOf(r), { quantity: left });
      delta += take;
    }
  };

  /**
   * Apply reads the rows FROM THE SERVER, so everything still waiting is sent
   * first — applying while a change is in flight would write the totals from
   * before it. A row that failed stops the apply: those totals would be wrong.
   */
  const [applyErr, setApplyErr] = useState('');
  const applyTotals = async () => {
    setBusy(true);
    setApplyErr('');
    try {
      if (!(await sync.flushAll())) {
        setApplyErr('Some rows did not save — retry them before applying.');
        return;
      }
      await api.applyInventoryTotals(leadId);
      onApplied();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  /**
   * The method picker is the REP's tag for how the inventory reached us —
   * photos, a video walkthrough, a typed list. It is NOT a submission.
   *
   * IT USED TO WRITE submitted_at AND MUST NOT. That column means exactly one
   * thing from P5a on: the CLIENT pressed Submit in their portal, and the
   * portal refuses every write while it is set. A rep tagging "photos" was
   * stamping it, which locked a client out of a list they had never opened and
   * made the lock fire on leads no client had ever touched. Migration 036
   * cleared the values this line wrote, and the backend now rejects a non-null
   * submitted_at from this endpoint outright (routes/inventory.ts).
   *
   * Only `method` and `submission_type` travel from here. Reopening — the one
   * move this endpoint still makes on submitted_at — is `reopen()` below.
   */
  const setMethod = async (method: string) => {
    setBusy(true);
    try {
      await api.setInventoryMethod(leadId, {
        method,
        submission_type: method,
      });
      await refreshInv();
    } finally {
      setBusy(false);
    }
  };

  // ---- Derived UI data ----------------------------------------------------

  /**
   * WHAT'S ON THIS JOB, NOT WHAT COULD BE.
   *
   * Both views used to render the entire 170-item catalog with a zero in the
   * quantity box, because both are also the sheet a rep ENTERS on. That is fine
   * on an empty lead and useless on a filled one: a client submission of twenty
   * lines arrives as twenty rows scattered through a hundred and seventy, and
   * reading it means scanning for the shaded ones.
   *
   * So the default is items only, and the catalog is still one keystroke away:
   *
   *   quantity > 0        always shown — this is the job
   *   search has text     the whole catalog is searched, so typing "sofa" finds
   *                       the row at zero and the rep sets a quantity on it. This
   *                       is the add path, and it must keep working.
   *   Show all            the old behaviour, on a toggle, for browsing the
   *                       catalogue when nobody remembers what it is called.
   *
   * Custom items are never filtered here: they exist only because someone added
   * them, so their quantity is > 0 by definition, and they are rendered from
   * their own list.
   */
  const filteredCatalog = useMemo(() => {
    // Whole-move items are drawn in their own group, first (sectionRows).
    // A hidden item (047) is offered nowhere — search only knows active items —
    // but one already on the job keeps its line here, so it can be seen,
    // corrected and still counted.
    const rest = catalog.filter((c) => !sectionIds.has(c.id));
    const onJob = (c: any) => num(itemByCatalogId[c.id]?.quantity) > 0;
    if (q) return rest.filter(matchesCat).sort(byRank);
    if (showAllCatalog) return rest.filter((c: any) => c.is_active !== false || onJob(c));
    return rest.filter(onJob);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, hitRank, showAllCatalog, itemByCatalogId, sectionIds]);

  /**
   * Room grouping: order by rooms.sort_order; a catalog item belongs to a room if
   * the room key is in common_in_rooms (fallback to category).
   *
   * PLUS EVERY ROW THE ROOM ACTUALLY HOLDS, which the catalog subset alone does
   * not cover: a custom item, or a catalog item the client filed in a room its
   * common_in_rooms never listed. Those rows exist, have a quantity, and were
   * invisible in this view — the room said "3 pcs" and showed two of them. A row
   * that cannot be seen cannot be corrected.
   *
   * Items with no room at all get their own group at the end, which is where a
   * custom item added from the cube sheet lives.
   */
  const roomGroups = useMemo(() => {
    const inSection = (id?: string | null) => !!id && sectionIds.has(id);
    const rowMatches = (it: ItemRow, cat: any) => (cat ? matchesCat(cat) : matchesName(it.custom_name ?? 'Item'));

    // The whole-move section is not a room: it is drawn first, on its own, and
    // its items are left out of every room below.
    const groups = rooms.filter((room) => !room.whole_move).map((room) => {
      // Active items only; a hidden one on the lead still shows below, as a row
      // the room actually holds.
      const subset = activeCatalog.filter((c) => {
        if (inSection(c.id)) return false;
        const common: string[] = Array.isArray(c.common_in_rooms) ? c.common_in_rooms : [];
        return common.includes(room.room_key) || c.category === room.room_key;
      });
      const listed = new Set(subset.map((c) => c.id));

      const entries: RoomEntry[] = subset
        .filter((c) => matchesCat(c)
          && (q || showAllCatalog || num(itemByCatalogId[c.id]?.quantity) > 0))
        .sort(byRank)
        .map((c) => ({ key: `c${c.id}`, cat: c, item: itemByCatalogId[c.id] ?? null }));

      for (const it of items) {
        if ((it.room_key ?? null) !== room.room_key) continue;
        if (inSection(it.catalog_item_id)) continue;                         // counted in the section
        if (it.catalog_item_id && listed.has(it.catalog_item_id)) continue;   // already above
        // catalogById holds whole catalog rows but is TYPED for the math engine,
        // which wants cuft and density and nothing else. The name is really there.
        const cat = it.catalog_item_id ? (catalogById[it.catalog_item_id] as any) ?? null : null;
        if (!rowMatches(it, cat)) continue;
        entries.push({ key: `x${it._key ?? it.id}`, cat, item: it });
      }

      return { room, entries };
    });

    const unroomed: RoomEntry[] = items
      .filter((it) => !it.room_key && !inSection(it.catalog_item_id))
      .filter((it) => rowMatches(it, it.catalog_item_id ? (catalogById[it.catalog_item_id] as any) ?? null : null))
      .map((it) => ({ key: `u${it._key ?? it.id}`, cat: it.catalog_item_id ? (catalogById[it.catalog_item_id] as any) ?? null : null, item: it }));

    return { groups, unroomed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeCatalog, catalogById, items, itemByCatalogId, hitRank, showAllCatalog, sectionIds]);

  const leadCuft = num(lead?.cubic_feet);
  const leadWeight = num(lead?.weight_lbs);
  const method = inv?.header?.method ?? null;

  // ---- What the CLIENT did (P5a) ------------------------------------------
  const header = inv?.header ?? null;
  const submittedAt: string | null = header?.submitted_at ?? null;
  const changeRequestedAt: string | null = header?.change_requested_at ?? null;
  const capturePath: string | null = header?.capture_path ?? null;

  // jsonb arrives as an object; a string is tolerated because a hand-edited row
  // or an older driver can deliver one, and a bad blob must not blank the panel.
  const clientNotes = useMemo<Record<string, string>>(() => {
    const raw = header?.client_notes;
    try {
      if (raw && typeof raw === 'object') return raw as Record<string, string>;
      if (typeof raw === 'string' && raw.trim()) return JSON.parse(raw);
    } catch { /* fall through */ }
    return {};
  }, [header]);

  const clientItems = useMemo(() => items.filter((it) => it.source === 'client'), [items]);
  const roomNameByKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of rooms) m[r.room_key] = r.name;
    return m;
  }, [rooms]);

  const reopen = async () => {
    setBusy(true);
    try {
      await api.reopenClientInventory(leadId);
      await refreshInv();
    } finally {
      setBusy(false);
    }
  };

  /**
   * CLEARING IS DELETING THE FILE, and it asks first. The row survives with who
   * cleared it, so "the client says they sent a video" stays answerable — but the
   * bytes are gone and no amount of asking us brings them back, which is exactly
   * the kind of action that should cost a confirmation.
   */
  const clearMedia = async (m: RepMedia) => {
    const what = m.kind === 'video' ? 'video' : 'photo';
    if (!window.confirm(`Delete this ${what} (${m.sizeLabel})? The file is removed for good; the record that it existed stays.`)) return;
    setBusy(true);
    try {
      await api.clearInventoryMedia(leadId, m.id);
      await refreshInv();
    } finally {
      setBusy(false);
    }
  };

  // ---- Render -------------------------------------------------------------

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.bg, width: '100%', maxWidth: '1040px', display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${C.border}`, borderRight: `1px solid ${C.border}` }}
      >
        {/* Header */}
        <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: C.text }}>
              Inventory · {lead?.first_name} {lead?.last_name}
              <span style={{ fontSize: '11px', color: C.dim, fontFamily: C.mono, marginLeft: '10px' }}>
                {formatJobNo(lead)}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
              Feeds the broker only — never shown on customer documents.
            </div>
          </div>
          {applyErr && <span style={{ fontSize: '11px', color: RED }}>{applyErr}</span>}
          <button
            onClick={applyTotals}
            disabled={busy}
            title={sync.busy ? 'Saves what is still waiting first, then applies' : undefined}
            style={{ padding: '8px 14px', minHeight: '36px', background: C.greenFill, border: 'none', color: 'var(--crm-on-green)', borderRadius: '7px', cursor: busy ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700, opacity: busy ? 0.6 : 1 }}
          >
            Apply totals to lead
          </button>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ padding: '8px 12px', background: C.panel2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
          >
            <Icons.close size={16} />
          </button>
        </div>

        {/* Toolbar: view toggle + search + method */}
        <div style={{ background: C.panel, borderBottom: `1px solid ${C.border}`, padding: '10px 18px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', borderRadius: '7px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
            {(['cube', 'room'] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{ padding: '6px 14px', background: view === v ? C.accentFill : C.panel2, color: view === v ? 'var(--crm-on-fill)' : C.muted, border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: view === v ? 700 : 400 }}
              >
                {v === 'cube' ? 'Cube Sheet' : 'Room-by-Room'}
              </button>
            ))}
          </div>

          {/* SEARCH IS IN BOTH VIEWS NOW. It is the add path — type a name, the
              catalog row appears at zero, set a quantity — and Room-by-Room needs
              it for the same reason the Cube Sheet does. */}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search catalog to add…"
            style={{ flex: 1, minWidth: '160px', padding: '7px 11px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '12px' }}
          />
          <button
            onClick={() => setShowAllCatalog((v) => !v)}
            title={showAllCatalog ? 'Show only what is on this job' : 'Show every catalog item, including quantity zero'}
            style={{ padding: '7px 12px', background: showAllCatalog ? C.accentFill : C.panel2, border: `1px solid ${showAllCatalog ? C.accentLine : C.border}`, color: showAllCatalog ? 'var(--crm-on-fill)' : C.muted, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
          >
            {showAllCatalog ? 'Items only' : `Show all ${catalog.length}`}
          </button>
          {view === 'cube' && (
            <button
              onClick={() => addCustom()}
              disabled={busy}
              style={{ padding: '7px 12px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}
            >
              + Add custom item
            </button>
          )}

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
            <span style={{ fontSize: '11px', color: C.dim }}>Sent us:</span>
            {['photos', 'video', 'text_list'].map((m) => (
              <button
                key={m}
                onClick={() => setMethod(m)}
                style={{ padding: '5px 9px', background: method === m ? `${C.amberTint}` : C.panel2, border: `1px solid ${method === m ? C.amberLine : C.border}`, color: method === m ? C.amber : C.muted, borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
              >
                {m === 'text_list' ? 'text list' : m}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {/* What the client sent, above the sheet the rep works in. It is the
              thing a rep opening this panel after a notification came to read,
              and it is read-only here: their words, not a field to edit. */}
          <ClientSubmission
            submittedAt={submittedAt}
            changeRequestedAt={changeRequestedAt}
            capturePath={capturePath}
            notes={clientNotes}
            clientItems={clientItems}
            roomNameByKey={roomNameByKey}
            catalogById={catalogById}
            media={inv?.media ?? []}
            mediaTotals={inv?.mediaTotals ?? null}
            busy={busy}
            onReopen={reopen}
            onClearMedia={clearMedia}
          />

          {noMatch && (
            <button
              onClick={() => { logMiss(q, 'add_own'); addCustom(q); setSearch(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: '12px', minHeight: '40px', padding: '9px 12px', background: `${C.amberTint}`, border: `1px dashed ${C.amberLine}`, color: C.text, borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}
            >
              <span style={{ color: C.muted }}>Can't find it?</span> Add “{q}” as a custom item
            </button>
          )}

          {view === 'cube' ? (
            <CubeSheet
              section={sectionRoom ? { name: sectionRoom.name, rows: sectionRows } : null}
              onSectionQty={setSectionTotal}
              catalog={filteredCatalog}
              itemByCatalogId={itemByCatalogId}
              customItems={filteredCustomItems}
              onQty={setCatalogQty}
              onPatchItem={patchItem}
              onRemove={removeItem}
              onRetry={retryItem}
            />
          ) : (
            <RoomByRoom
              section={sectionRoom ? { name: sectionRoom.name, rows: sectionRows } : null}
              onSectionQty={setSectionTotal}
              groups={roomGroups.groups}
              unroomed={roomGroups.unroomed}
              openRooms={openRooms}
              setOpenRooms={setOpenRooms}
              showingAll={showAllCatalog || !!search.trim()}
              onQty={setCatalogQty}
              onPatchItem={patchItem}
              onRemove={removeItem}
              onRetry={retryItem}
            />
          )}
        </div>

        {/* Footer totals bar */}
        <div style={{ background: C.panel, borderTop: `1px solid ${C.border}`, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ fontFamily: C.mono, fontSize: '13px', color: C.text }}>
            <span style={{ color: C.accent, fontWeight: 700 }}>INVENTORY TOTAL </span>
            {fmt1(totals.totalCuft)} CuFT · {fmt0(totals.totalWeight)} lbs ·{' '}
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
            {pieceCount !== itemCount && <span style={{ color: C.muted }}> ({pieceCount} pieces)</span>}
            {/* Computed here from the rows on screen, by the server's own engine,
                so it is right for what is shown — but not yet what the server
                holds until the batch lands. Say so rather than look final. */}
            {sync.failed > 0
              ? <span style={{ color: RED }}> · {sync.failed} not saved</span>
              : sync.busy && <span style={{ color: C.amber }}> · saving…</span>}
          </div>
          <div style={{ marginLeft: 'auto', fontFamily: C.mono, fontSize: '12px', color: C.muted }}>
            Lead now: <span style={{ color: C.text }}>{fmt0(leadCuft)} CuFT · {fmt0(leadWeight)} lbs</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- What the client sent (P5a) ---------------------------------------------

const CLIENT = 'var(--crm-violet)';   // violet — theirs. Blue is ours, amber is a warning.

/** Notes keys that are not a room. Must match the client page. */
const WRITTEN_LIST = 'written_list';
const GENERAL = 'general';

/**
 * THE NAMES THE CLIENT IS SHOWN, exactly — see pages/portal/InventoryRequestView.
 * A rep reading this line may well be on the phone with the person who picked it,
 * and two vocabularies for one choice is how that call goes wrong. The stored
 * values keep their original spelling; only what a human reads is aligned.
 */
const PATH_LABEL: Record<string, string> = {
  rooms: 'Room by room',
  cube_sheet: 'Full list',
  catalog: 'Cube sheet',   // 044 — 'cube_sheet' was already taken by Full list
};

/**
 * The client's half of the inventory, read-only, above the rep's sheet.
 *
 * RENDERS NOTHING WHEN THE CLIENT HAS DONE NOTHING. Most leads are rep-entered
 * and always will be; a permanent empty "Client submission" card on every one
 * of them is chrome that teaches people to scroll past the place the real news
 * appears.
 *
 * REOPENING IS TWO CLICKS. Clearing submitted_at is the only way back in for
 * the client, and it DISCARDS the timestamp — there is no second column holding
 * a copy, and nothing but a fresh client submission will put one back. A
 * mis-click on a one-click button costs a fact we cannot recover, so it asks.
 */
function ClientSubmission(props: {
  submittedAt: string | null;
  changeRequestedAt: string | null;
  capturePath: string | null;
  notes: Record<string, string>;
  clientItems: ItemRow[];
  roomNameByKey: Record<string, string>;
  catalogById: Record<string, CatalogItem>;
  media: RepMedia[];
  mediaTotals: { count: number; bytes: number; label: string } | null;
  busy: boolean;
  onReopen: () => void;
  onClearMedia: (m: RepMedia) => void;
}) {
  const { submittedAt, changeRequestedAt, capturePath, notes, clientItems, roomNameByKey, catalogById, media, mediaTotals, busy, onReopen, onClearMedia } = props;
  const [confirming, setConfirming] = useState(false);

  const noteEntries = Object.entries(notes).filter(([, v]) => String(v ?? '').trim());
  const written = String(notes[WRITTEN_LIST] ?? '').trim();
  const general = String(notes[GENERAL] ?? '').trim();
  const roomNotes = noteEntries.filter(([k]) => k !== WRITTEN_LIST && k !== GENERAL);

  const hasAnything = !!submittedAt || !!changeRequestedAt || clientItems.length > 0 || noteEntries.length > 0 || media.length > 0;
  if (!hasAnything) return null;

  const pieces = clientItems.reduce((s, it) => s + num(it.quantity), 0);

  return (
    <div style={{ border: `1px solid ${changeRequestedAt ? C.amberLine : CLIENT}`, borderRadius: '9px', marginBottom: '14px', overflow: 'hidden' }}>
      {/* Banner */}
      <div style={{ background: changeRequestedAt ? `${C.amberTint}` : `${tintOf(CLIENT, 10)}`, padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ color: changeRequestedAt ? C.amber : CLIENT, display: 'inline-flex' }}>
          {changeRequestedAt ? <Icons.overdue size={17} /> : <Icons.accepted size={17} />}
        </span>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: C.text }}>
            {changeRequestedAt
              ? 'The client asked to change this list'
              : submittedAt
                ? 'The client submitted this inventory'
                : 'The client has started this inventory'}
          </div>
          <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
            {submittedAt && <>Sent {formatDateTime(submittedAt)}</>}
            {changeRequestedAt && <> · asked {formatDateTime(changeRequestedAt)}</>}
            {capturePath && <> · filled in: {PATH_LABEL[capturePath] ?? capturePath}</>}
            {!submittedAt && !changeRequestedAt && <>Not submitted yet — they can still edit it.</>}
          </div>
        </div>

        {/* Only a SUBMITTED inventory can be reopened; an unsubmitted one is
            already open, and offering the button would imply otherwise. */}
        {submittedAt && (
          confirming ? (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: C.muted, maxWidth: '260px' }}>
                Reopen for editing? The submission time is discarded.
              </span>
              <button
                onClick={() => { setConfirming(false); onReopen(); }}
                disabled={busy}
                style={{ padding: '6px 11px', background: C.amberFill, border: 'none', color: 'var(--crm-on-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
              >
                Reopen
              </button>
              <button
                onClick={() => setConfirming(false)}
                style={{ padding: '6px 11px', background: C.panel2, border: `1px solid ${C.border}`, color: C.muted, borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              disabled={busy}
              style={{ padding: '7px 12px', background: C.panel2, border: `1px solid ${changeRequestedAt ? C.amberLine : C.border}`, color: changeRequestedAt ? C.amber : C.text, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
            >
              Reopen for the client
            </button>
          )
        )}
      </div>

      {/* Their words and their rows */}
      <div style={{ padding: '12px 14px', background: C.panel, display: 'grid', gap: '12px' }}>
        {clientItems.length > 0 && (
          <div>
            <SubHead>
              They added {clientItems.length} {clientItems.length === 1 ? 'line' : 'lines'}
              {pieces !== clientItems.length && ` · ${pieces} pieces`}
            </SubHead>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px' }}>
              {clientItems.map((it) => {
                const cat = it.catalog_item_id ? catalogById[it.catalog_item_id] : null;
                return (
                  <div key={it.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '6px 9px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px' }}>
                    <span style={{ color: CLIENT, display: 'inline-flex', paddingTop: '1px' }}>
                      <CatalogIcon name={(cat as any)?.icon ?? null} size={15} stroke={1.9} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', color: C.text }}>
                        {it.custom_name || (cat as any)?.name || 'Item'}
                        <span style={{ fontFamily: C.mono, color: C.muted }}> ×{num(it.quantity)}</span>
                      </div>
                      {it.room_key && (
                        <div style={{ fontSize: '10px', color: C.dim }}>{roomNameByKey[it.room_key] ?? it.room_key}</div>
                      )}
                      {it.notes && (
                        <div style={{ fontSize: '11px', color: CLIENT, marginTop: '2px' }}>“{it.notes}”</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {media.length > 0 && (
          <div>
            <SubHead>
              Photos &amp; video{mediaTotals ? ` — ${mediaTotals.count} file${mediaTotals.count === 1 ? '' : 's'}, ${mediaTotals.label}` : ''}
            </SubHead>
            <RepMediaGrid media={media} busy={busy} onClear={onClearMedia} />
          </div>
        )}

        {written && (
          <div>
            <SubHead>The list they typed</SubHead>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px', color: C.text, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '9px 11px' }}>
              {written}
            </div>
          </div>
        )}

        {roomNotes.length > 0 && (
          <div>
            <SubHead>Notes by room</SubHead>
            <div style={{ display: 'grid', gap: '6px' }}>
              {roomNotes.map(([key, text]) => (
                <div key={key} style={{ fontSize: '12px', color: C.text, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '8px 11px' }}>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: C.dim, marginRight: '7px' }}>
                    {roomNameByKey[key] ?? key}
                  </span>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {general && (
          <div>
            <SubHead>Notes for the team</SubHead>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px', color: C.text, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '9px 11px' }}>
              {general}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One file as the rep's panel sees it — shaped by GET /api/leads/:id/inventory. */
export interface RepMedia {
  id: string;
  kind: 'photo' | 'video';
  url: string | null;
  downloadUrl: string | null;
  caption: string | null;
  mime: string;
  sizeBytes: number;
  sizeLabel: string;
  originalName: string | null;
  roomKey: string | null;
  recordedInPortal: boolean;
  source: 'client' | 'rep';
  createdAt: string;
}

/**
 * WHAT A REP CAN ACTUALLY OPEN, which is not the same as what a client can send.
 *
 * VIDEO PLAYS INLINE AND HAS A DOWNLOAD BUTTON BESIDE IT, always — not as a
 * fallback for an error we detect, because we cannot detect this one. An iPhone
 * records HEVC in a .mov container, Chrome on Windows will not decode it, and the
 * failure is a black rectangle with working controls: no event, no error, nothing
 * to branch on. So the escape hatch is permanent and visible, and the file opens
 * in whatever the desktop uses for video.
 *
 * HEIC PHOTOS ARE THE SAME STORY, worse: Safari renders them, Chrome and Firefox
 * render nothing at all. <img> does fire onError there, so a HEIC that will not
 * display says so in words and offers the download, rather than showing a broken
 * image icon and leaving the rep to guess.
 */
function RepMediaGrid({ media, busy, onClear }: {
  media: RepMedia[]; busy: boolean; onClear: (m: RepMedia) => void;
}) {
  const [failed, setFailed] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))', gap: '9px' }}>
      {media.map((m) => {
        const broken = failed[m.id];
        return (
          <div key={m.id} style={{ border: `1px solid ${C.border}`, borderRadius: '7px', overflow: 'hidden', background: C.panel2 }}>
            {m.kind === 'video' ? (
              m.url ? (
                <video
                  src={m.url} controls preload="metadata" playsInline
                  style={{ width: '100%', aspectRatio: '4 / 3', background: 'var(--crm-overlay)', display: 'block' }}
                />
              ) : <Unavailable />
            ) : m.url && !broken ? (
              <button
                type="button"
                onClick={() => setOpen(open === m.id ? null : m.id)}
                style={{ border: 'none', padding: 0, margin: 0, cursor: 'zoom-in', display: 'block', width: '100%', background: C.panel2 }}
                title="Open full size"
              >
                <img
                  src={m.url} alt={m.caption ?? ''} loading="lazy"
                  onError={() => setFailed((f) => ({ ...f, [m.id]: true }))}
                  style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
                />
              </button>
            ) : (
              <div style={{ aspectRatio: '4 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '10px', fontSize: '10.5px', color: C.dim, lineHeight: 1.45 }}>
                {broken
                  ? `This browser can't show ${m.mime.replace('image/', '').toUpperCase()}. Download it to view.`
                  : 'Not available'}
              </div>
            )}

            <div style={{ padding: '7px 8px', fontSize: '10.5px', color: C.dim, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ color: C.muted }}>
                {m.kind === 'video' ? 'Video' : 'Photo'} · {m.sizeLabel}
                {m.recordedInPortal && ' · recorded in portal'}
                {m.source === 'rep' && ' · ours'}
              </span>
              {m.caption && <span style={{ color: C.text }}>{m.caption}</span>}
              {m.originalName && <span style={{ wordBreak: 'break-all' }}>{m.originalName}</span>}
              <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                {m.downloadUrl && (
                  <a href={m.downloadUrl} style={{ ...iconBtnLink }} title="Download the original file">Download</a>
                )}
                <button
                  type="button" disabled={busy} onClick={() => onClear(m)}
                  style={{ ...iconBtnLink, color: C.dim, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                  title="Delete the file and free the storage"
                >
                  Clear
                </button>
              </div>
            </div>

            {open === m.id && m.url && (
              <div
                onClick={() => setOpen(null)}
                style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', cursor: 'zoom-out' }}
              >
                <img src={m.url} alt={m.caption ?? ''} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const Unavailable = () => (
  <div style={{ aspectRatio: '4 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.5px', color: C.dim }}>
    Not available
  </div>
);

const iconBtnLink: React.CSSProperties = { fontSize: '10.5px', fontWeight: 700, color: C.accent, textDecoration: 'none' };

const SubHead = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', color: C.dim, marginBottom: '6px', fontWeight: 700 }}>
    {children}
  </div>
);

/** The mark on a row the client entered. Same violet everywhere it appears. */
const ClientChip = () => (
  <span
    title="Added by the client through their portal"
    style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: CLIENT, border: `1px solid ${tintOf(CLIENT, 40)}`, borderRadius: '999px', padding: '1px 6px', marginLeft: '6px', whiteSpace: 'nowrap' }}
  >
    client
  </span>
);

// ---- Cube Sheet -------------------------------------------------------------

/** The whole-move section (Boxes / Misc) as the views draw it: one line per item, its total. */
type SectionView = { name: string; rows: { cat: any; qty: number; failed: ItemRow | null }[] } | null;

function CubeSheet(props: {
  section: SectionView;
  onSectionQty: (catId: string, qty: number) => void;
  catalog: any[];
  itemByCatalogId: Record<string, ItemRow>;
  customItems: ItemRow[];
  onQty: (catId: string, qty: number) => void;
  onPatchItem: (id: string, patch: Record<string, any>) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const { section, onSectionQty, catalog, itemByCatalogId, customItems, onQty, onPatchItem, onRemove, onRetry } = props;

  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: '10px', color: C.dim, textTransform: 'uppercase', letterSpacing: '0.5px', position: 'sticky', top: 0, background: C.bg, borderBottom: `1px solid ${C.border}` };
  const td: React.CSSProperties = { padding: '5px 8px', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.panel}` };
  const tdNum: React.CSSProperties = { ...td, fontFamily: C.mono, color: C.muted, textAlign: 'right' };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Item</th>
          <th style={{ ...th, textAlign: 'right' }}>CuFT</th>
          <th style={{ ...th, textAlign: 'right' }}>Density</th>
          <th style={{ ...th, textAlign: 'center', width: '90px' }}>Qty</th>
          <th style={{ ...th, textAlign: 'right' }}>Weight</th>
        </tr>
      </thead>
      <tbody>
        {/* Boxes / Misc first: one line per item, the TOTAL across every row
            for it whatever room it was filed under. */}
        {section && section.rows.length > 0 && (
          <>
            <tr>
              <td colSpan={5} style={{ ...td, fontSize: '10px', fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.5px', background: `${C.amberTint}` }}>
                {section.name} <span style={{ color: C.dim, textTransform: 'none', fontWeight: 400 }}>· whole move</span>
              </td>
            </tr>
            {section.rows.map(({ cat, qty, failed }) => {
              const cuft = num(cat.cuft);
              const density = cat.density_lbs_per_cuft != null ? num(cat.density_lbs_per_cuft) : 7;
              return (
                <tr key={`s${cat.id}`} style={failed ? { background: `${tintOf(RED, 8)}` } : qty > 0 ? { background: `${C.amberTint}` } : undefined}>
                  <td style={td}>
                    {cat.name}
                    {failed && <RowError row={failed} onRetry={() => onRetry(failed.id)} />}
                  </td>
                  <td style={tdNum}>{cuft}</td>
                  <td style={tdNum}>{density}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <QtyInput value={qty} onChange={(v) => onSectionQty(cat.id, v)} />
                  </td>
                  <td style={tdNum}>{qty > 0 ? fmt0(cuft * density * qty) : '—'}</td>
                </tr>
              );
            })}
          </>
        )}

        {/* Custom items (editable name/cuft/density) */}
        {customItems.map((it) => {
          const qty = num(it.quantity);
          const cuft = num(it.custom_cuft);
          const density = it.custom_density != null ? num(it.custom_density) : 7;
          return (
            <tr key={it._key ?? it.id} style={it._status === 'error' ? { background: `${tintOf(RED, 8)}` } : undefined}>
              <td style={td}>
                <input
                  defaultValue={it.custom_name ?? ''}
                  onBlur={(e) => e.target.value !== (it.custom_name ?? '') && onPatchItem(it.id, { custom_name: e.target.value })}
                  style={{ width: '90%', minHeight: '36px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '6px 8px', fontSize: '12px' }}
                />
                <span style={{ fontSize: '10px', color: C.amber, marginLeft: '6px' }}>custom</span>
                {it.source === 'client' && <ClientChip />}
                {it.notes && <div style={{ fontSize: '11px', color: it.source === 'client' ? CLIENT : C.muted, marginTop: '2px' }}>“{it.notes}”</div>}
                <RowError row={it} onRetry={() => onRetry(it.id)} />
              </td>
              <td style={tdNum}>
                <NumericField
                  value={cuft}
                  disabled={false}
                  onCommit={(n) => onPatchItem(it.id, { custom_cuft: n })}
                  style={{ width: '60px', minHeight: '36px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '6px', fontSize: '12px', textAlign: 'right' }}
                />
              </td>
              <td style={tdNum}>
                <NumericField
                  value={density}
                  disabled={false}
                  onCommit={(n) => onPatchItem(it.id, { custom_density: n })}
                  style={{ width: '52px', minHeight: '36px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '6px', fontSize: '12px', textAlign: 'right' }}
                />
              </td>
              <td style={{ ...td, textAlign: 'center' }}>
                <QtyInput
                  value={qty}
                  onChange={(q) => (q <= 0 ? onRemove(it.id) : onPatchItem(it.id, { quantity: q }))}
                />
              </td>
              <td style={tdNum}>{fmt0(cuft * density * qty)}</td>
            </tr>
          );
        })}

        {catalog.map((cat) => {
          const it = itemByCatalogId[cat.id];
          const qty = it ? num(it.quantity) : 0;
          const cuft = num(cat.cuft);
          const density = cat.density_lbs_per_cuft != null ? num(cat.density_lbs_per_cuft) : 7;
          const mine = it?.source === 'client';
          return (
            <tr key={cat.id} style={it?._status === 'error' ? { background: `${tintOf(RED, 8)}` } : qty > 0 ? { background: mine ? `${tintOf(CLIENT, 8)}` : `${C.accentTint}` } : undefined}>
              <td style={td}>
                {cat.name}
                {mine && <ClientChip />}
                {/* The per-item note. It has been stored since 005 and has
                    never been rendered anywhere. Violet only when the CLIENT
                    wrote it — colouring a rep's own note as theirs would
                    attribute our words to them. */}
                {it?.notes && <div style={{ fontSize: '11px', color: mine ? CLIENT : C.muted, marginTop: '2px' }}>“{it.notes}”</div>}
                {it && <RowError row={it} onRetry={() => onRetry(it.id)} />}
              </td>
              <td style={tdNum}>{cuft}</td>
              <td style={tdNum}>{density}</td>
              <td style={{ ...td, textAlign: 'center' }}>
                <QtyInput value={qty} onChange={(q) => onQty(cat.id, q)} />
              </td>
              <td style={tdNum}>{qty > 0 ? fmt0(cuft * density * qty) : '—'}</td>
            </tr>
          );
        })}
        {catalog.length === 0 && customItems.length === 0 && !(section && section.rows.length) && (
          <tr>
            <td style={{ ...td, color: C.dim }} colSpan={5}>
              No catalog items — run migration 005 seed for your company.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ---- Room-by-Room -----------------------------------------------------------

/**
 * Room-by-Room.
 *
 * ITEMS ONLY BY DEFAULT, like the Cube Sheet — with the search box and the "Show
 * all" toggle as the two ways back to the full catalogue. `showingAll` says which
 * mode we are in, and it changes only what the empty states say: a room with
 * nothing on the job reads differently from a room with no matches for "sofa".
 *
 * A ROOM THAT HOLDS NOTHING DISAPPEARS while filtering. Eleven collapsed headers
 * with nothing inside them is the same scanning problem one level up.
 *
 * EVERY LINE IS A RoomEntry, so a custom item and a catalog item the client filed
 * somewhere unexpected are both visible and both settable. A catalog line commits
 * through onQty(catId) — which creates the row, files it in this room, and deletes
 * it at zero — and a row without a catalog entry commits through its own id.
 */
function RoomByRoom(props: {
  section: SectionView;
  onSectionQty: (catId: string, qty: number) => void;
  groups: { room: any; entries: RoomEntry[] }[];
  unroomed: RoomEntry[];
  openRooms: Record<string, boolean>;
  setOpenRooms: (fn: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  showingAll: boolean;
  onQty: (catId: string, qty: number, roomKey?: string | null) => void;
  onPatchItem: (id: string, patch: Record<string, any>) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const { section, onSectionQty, groups, unroomed, openRooms, setOpenRooms, showingAll, onQty, onPatchItem, onRemove, onRetry } = props;

  const setQty = (e: RoomEntry, roomKey: string | null, q: number) => {
    if (e.cat && (!e.item || e.item.catalog_item_id)) return onQty(e.cat.id, q, roomKey);
    if (e.item) return q <= 0 ? onRemove(e.item.id) : onPatchItem(e.item.id, { quantity: q });
  };

  const line = (e: RoomEntry, roomKey: string | null) => {
    const qty = num(e.item?.quantity);
    const mine = e.item?.source === 'client';
    const custom = !e.cat;
    const name = e.cat?.name ?? e.item?.custom_name ?? 'Item';
    const cuft = custom ? num(e.item?.custom_cuft) : num(e.cat?.cuft);
    return (
      <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: qty > 0 ? (mine ? `${tintOf(CLIENT, 12)}` : `${C.accentTint}`) : C.panel, border: `1px solid ${mine ? `${tintOf(CLIENT, 40)}` : C.border}`, borderRadius: '7px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '12px', color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
            {custom && <span style={{ fontSize: '10px', color: C.amber, marginLeft: '6px' }}>custom</span>}
            {mine && <ClientChip />}
          </div>
          <div style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>{cuft} CuFT</div>
          {e.item?.notes && <div style={{ fontSize: '11px', color: mine ? CLIENT : C.muted, marginTop: '2px' }}>“{e.item.notes}”</div>}
          {e.item && <RowError row={e.item} onRetry={() => onRetry(e.item!.id)} />}
        </div>
        <QtyInput value={qty} onChange={(q) => setQty(e, roomKey, q)} />
      </div>
    );
  };

  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px', padding: '10px 14px', background: C.bg };
  const visible = groups.filter((g) => g.entries.length > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Boxes / Misc: one section for the whole move, before the rooms. */}
      {section && section.rows.length > 0 && (
        <div style={{ border: `1px solid ${C.amberSoftLine}`, borderRadius: '9px', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: C.panel, fontSize: '13px', fontWeight: 600, color: C.text }}>
            {section.name}
            <span style={{ fontSize: '11px', color: C.dim, marginLeft: '8px' }}>whole move</span>
            {(() => { const n = section.rows.reduce((t, r) => t + r.qty, 0); return n > 0 ? <span style={{ fontSize: '11px', color: C.accent, fontFamily: C.mono }}> · {n} pcs</span> : null; })()}
          </div>
          <div style={grid}>
            {section.rows.map(({ cat, qty, failed }) => (
              <div key={`s${cat.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', background: failed ? `${tintOf(RED, 8)}` : qty > 0 ? `${C.amberTint}` : C.panel, border: `1px solid ${C.border}`, borderRadius: '7px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.name}</div>
                  <div style={{ fontSize: '10px', color: C.dim, fontFamily: C.mono }}>{num(cat.cuft)} CuFT</div>
                  {failed && <RowError row={failed} onRetry={() => onRetry(failed.id)} />}
                </div>
                <QtyInput value={qty} onChange={(v) => onSectionQty(cat.id, v)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {visible.map(({ room, entries }) => {
        const isOpen = openRooms[room.room_key] ?? true;
        const roomQty = entries.reduce((s, e) => s + num(e.item?.quantity), 0);
        const onJob = entries.filter((e) => num(e.item?.quantity) > 0).length;
        return (
          <div key={room.id} style={{ border: `1px solid ${C.border}`, borderRadius: '9px', overflow: 'hidden' }}>
            <button
              onClick={() => setOpenRooms((prev) => ({ ...prev, [room.room_key]: !isOpen }))}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: C.panel, border: 'none', color: C.text, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
            >
              <span style={{ color: C.muted }}>{isOpen ? '▾' : '▸'}</span>
              {room.name}
              {roomQty > 0 && (
                <span style={{ fontSize: '11px', color: C.accent, fontFamily: C.mono }}>· {roomQty} pcs</span>
              )}
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: C.dim }}>
                {showingAll ? `${onJob} of ${entries.length} items` : `${entries.length} item${entries.length === 1 ? '' : 's'}`}
              </span>
            </button>
            {isOpen && <div style={grid}>{entries.map((e) => line(e, room.room_key))}</div>}
          </div>
        );
      })}

      {/* Rows with no room: a custom item added from the Cube Sheet, or anything a
          client entered through the full-list path. They were invisible here. */}
      {unroomed.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: '9px', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', background: C.panel, fontSize: '13px', fontWeight: 600, color: C.text }}>
            Not in a room
            <span style={{ marginLeft: 'auto', fontSize: '11px', color: C.dim, float: 'right' }}>{unroomed.length} item{unroomed.length === 1 ? '' : 's'}</span>
          </div>
          <div style={grid}>{unroomed.map((e) => line(e, null))}</div>
        </div>
      )}

      {visible.length === 0 && unroomed.length === 0 && !(section && section.rows.length) && (
        <div style={{ fontSize: '12px', color: C.dim }}>
          {groups.length === 0
            ? 'No rooms — run migration 005 seed for your company.'
            : showingAll
              ? 'Nothing matches that search.'
              : 'Nothing on this job yet. Search for an item, or press “Show all” to browse the catalogue.'}
        </div>
      )}
    </div>
  );
}

/**
 * − / number / +. Never disabled while something saves: the change is on
 * screen at once and saved behind (useRowSync), and a stepper that greys out
 * per round trip is what made twenty quick changes take twenty round trips.
 * 36px targets — the 22px buttons were easy to miss at speed.
 */
function QtyInput({ value, onChange }: { value: number; onChange: (q: number) => void }) {
  const [local, setLocal] = useState<string>(value === 0 ? '' : String(value));
  const lastExternal = useRef(value);

  useEffect(() => {
    if (value !== lastExternal.current) {
      lastExternal.current = value;
      setLocal(value === 0 ? '' : String(value));
    }
  }, [value]);

  const current = Math.max(0, Math.trunc(Number(local) || 0));

  const commit = () => {
    const q = Math.max(0, Math.trunc(Number(local) || 0));
    setLocal(q === 0 ? '' : String(q));
    lastExternal.current = q;
    if (q !== value) onChange(q);
  };

  const step = (delta: number) => {
    const q = Math.max(0, current + delta);
    setLocal(q === 0 ? '' : String(q));
    lastExternal.current = q;
    if (q !== value) onChange(q);
  };

  const btn: React.CSSProperties = { width: '36px', height: '36px', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '16px', lineHeight: 1, flex: '0 0 auto' };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <button
        aria-label="One fewer"
        onClick={() => step(-1)}
        disabled={current <= 0}
        style={{ ...btn, cursor: current <= 0 ? 'default' : 'pointer', opacity: current <= 0 ? 0.4 : 1 }}
      >
        −
      </button>
      <input
        {...numberInput(1)}
        inputMode="numeric"
        aria-label="Quantity"
        value={local}
        placeholder="0"
        onChange={(e) => setLocal(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        style={{ width: '48px', height: '36px', textAlign: 'center', background: current > 0 ? C.panel2 : C.panel, border: `1px solid ${C.border}`, color: C.text, borderRadius: '7px', fontSize: '13px', fontFamily: C.mono }}
      />
      <button aria-label="One more" onClick={() => step(1)} style={{ ...btn, cursor: 'pointer' }}>
        +
      </button>
    </div>
  );
}

const RED = 'var(--crm-red)';

/** A row whose save failed: the edit stays on screen, this says so, Retry resends. */
function RowError({ row, onRetry }: { row: ItemRow; onRetry: () => void }) {
  if (row._status !== 'error') return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '11px', color: RED }}>
      <span>Not saved{row._error ? ` — ${row._error}` : ''}</span>
      <button
        onClick={onRetry}
        style={{ minHeight: '30px', padding: '4px 10px', background: 'transparent', border: `1px solid ${RED}`, color: RED, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
      >
        Retry
      </button>
    </div>
  );
}

// Commit-on-blur numeric field for custom-item cuft/density (float) and any
// other free numeric entry. Same local-state pattern as QtyInput: type freely,
// persist on blur / Enter; invalid/empty -> 0, negative clamped to 0.
function NumericField({
  value,
  disabled,
  integer,
  onCommit,
  style,
}: {
  value: number;
  disabled?: boolean;
  integer?: boolean;
  onCommit: (n: number) => void;
  style?: React.CSSProperties;
}) {
  const seed = (v: number) => (v == null || Number.isNaN(v) ? '' : String(v));
  const [local, setLocal] = useState<string>(seed(value));
  const lastExternal = useRef(value);

  useEffect(() => {
    if (value !== lastExternal.current) {
      lastExternal.current = value;
      setLocal(seed(value));
    }
  }, [value]);

  const commit = () => {
    const parsed = integer ? Math.trunc(Number(local)) : Number(local);
    const n = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    setLocal(String(n));
    lastExternal.current = n;
    if (n !== value) onCommit(n);
  };

  return (
    <input
      {...numberInput(1)}
      value={local}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={style}
    />
  );
}
